import { Children, FC, ReactNode, isValidElement, useCallback, useLayoutEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

interface RateFieldsRowProps {
  children: ReactNode;
}

export const RateFieldsRow: FC<RateFieldsRowProps> = ({ children }) => {
  const theme = useTheme();
  const isRow = useMediaQuery(theme.breakpoints.up("md"));

  const fields = Children.toArray(children).filter(isValidElement);
  const columnsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [topReservations, setTopReservations] = useState<number[]>([]);

  const alignInputs = useCallback(() => {
    if (!isRow) {
      setTopReservations([]);
      return;
    }

    const columns = columnsRef.current.slice(0, fields.length);
    const labelHeights = columns.map((column) => {
      const input = column?.querySelector("input, textarea");
      if (!column || !input) {
        return 0;
      }
      return input.getBoundingClientRect().top - column.getBoundingClientRect().top;
    });

    const tallestLabel = Math.max(0, ...labelHeights);
    setTopReservations(labelHeights.map((height) => tallestLabel - height));
  }, [isRow, fields.length]);

  useLayoutEffect(() => {
    alignInputs();
    if (!isRow || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(alignInputs);
    columnsRef.current.slice(0, fields.length).forEach((column) => column && observer.observe(column));
    return () => observer.disconnect();
  }, [alignInputs, isRow, fields.length]);

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "flex-start" }}>
      {fields.map((field, index) => (
        <Box
          key={isValidElement(field) ? (field.key ?? index) : index}
          ref={(node: HTMLDivElement | null) => {
            columnsRef.current[index] = node;
          }}
          sx={{ flex: 1, minWidth: 0, width: "100%", marginTop: isRow ? `${topReservations[index] ?? 0}px` : 0 }}
        >
          {field}
        </Box>
      ))}
    </Stack>
  );
};
